import type { IntlShape } from 'react-intl'

/** Anchor ID for the "Discover a world of rewards" tiers section on the status match page. */
export const DISCOVER_TIERS_ID = 'discover-tiers'

export function getLoyaltyEsimGbAmount(benefit: App.LoyaltyBenefit | undefined): number | undefined {
  return benefit?.value?.unit === 'GB' ? benefit.value.amount : undefined
}

export function formatLoyaltyEsimInternationalDataHeading(
  intl: IntlShape,
  amount: number | string,
): string {
  return intl.formatMessage(
    {
      defaultMessage: '{amount} GB Free International Data',
      id: 'oTKHBJeWGL-bar8FwzXj',
      description: 'Heading for eSIM benefit tile on account landing page',
    },
    { amount },
  )
}

/**
 * Returns a complete, i18n-friendly benefit label for a loyalty tier benefit.
 * The count, unit (GB / %), and noun are composed in a single ICU message so
 * translators can reorder them per locale. Returns undefined for unknown codes.
 *
 * Shared between the tier upgrade campaign cards, status match tier cards,
 * and the status match success modal benefits list.
 */
export function formatBenefitLabel(
  intl: IntlShape,
  benefit: App.LoyaltyRegionTierBenefit,
): string | undefined {
  const count = benefit.limit ?? 0
  const isUnlimited = benefit.unlimited ? 'true' : 'false'
  switch (benefit.code) {
    case 'hotel_upgrade':
      return intl.formatMessage({
        id: 'tA651M_6bS2zylNLDKxv',
        defaultMessage: '{isUnlimited, select, true {Unlimited} other {{count}}} free room or package {count, plural, one {upgrade} other {upgrades}}',
        description: 'Tier card benefit description for room upgrades with count',
      }, { isUnlimited, count })
    case 'lounge_pass':
      return intl.formatMessage({
        id: '2MxkSe5nXg3ic3dRs3lL',
        defaultMessage: '{isUnlimited, select, true {Unlimited} other {{count}}} free airport lounge {count, plural, one {pass} other {passes}}',
        description: 'Tier card benefit description for lounge passes with count',
      }, { isUnlimited, count })
    case 'airport_transfer':
      return intl.formatMessage({
        id: '-CQClq-RCgSzMjlv0q0e',
        defaultMessage: '{isUnlimited, select, true {Unlimited} other {{count}}} free airport chauffeur {count, plural, one {transfer} other {transfers}}',
        description: 'Tier card benefit description for airport transfers with count',
      }, { isUnlimited, count })
    case 'tour_exp_credit':
      return intl.formatMessage({
        id: 'MVqyjZyAGayqOSwcg4yT',
        defaultMessage: '{hasValue, select, true {{value}} other {{isUnlimited, select, true {Unlimited} other {{count}}}}} tour {count, plural, one {credit} other {credits}}',
        description: 'Tier card benefit description for tour credits with count',
      }, { hasValue: benefit.value ? 'true' : 'false', value: benefit.value ? `$${benefit.value.amount}` : '', isUnlimited, count })
    case 'travelshoot_credit':
      return intl.formatMessage({
        id: 'yAmTnBwT2kMZV5s-NSKn',
        defaultMessage: '{hasValue, select, true {{value}} other {{isUnlimited, select, true {Unlimited} other {{count}}}}} TravelShoot {count, plural, one {voucher} other {vouchers}}',
        description: 'Tier card benefit description for TravelShoot vouchers with count',
      }, { hasValue: benefit.value ? 'true' : 'false', value: benefit.value ? `$${benefit.value.amount}` : '', isUnlimited, count })
    case 'phone_support':
      return intl.formatMessage({
        id: 'qWYrWOkV8rz1f6WYS5bV',
        defaultMessage: '24/7 priority phone support',
        description: 'Tier card benefit description for 24/7 priority phone support',
      })
    case 'discounted_insurance':
      return intl.formatMessage({
        id: 'DTJtZ-VSAHSsZrB360iy',
        defaultMessage: '{discount}% off travel insurance',
        description: 'Tier card benefit description for discounted travel insurance',
      }, { discount: benefit.value?.amount ?? 0 })
    case 'esim_package':
      return intl.formatMessage({
        id: 'hmq_cfLuUUgFdWYt5T1d',
        defaultMessage: '{amount}GB travel eSIM per international trip',
        description: 'Tier card benefit description for complimentary travel eSIM data package',
      }, { amount: benefit.value?.amount ?? 0 })
    default:
      return undefined
  }
}

/**
 * Preferred display order for loyalty benefits on the Status Match page.
 * Codes not listed here keep their original order at the end.
 */
const BENEFIT_DISPLAY_ORDER: Array<App.LoyaltyBenefitType> = [
  'hotel_upgrade',
  'airport_transfer',
  'lounge_pass',
  'tour_exp_credit',
  'travelshoot_credit',
  'esim_package',
  'phone_support',
  'discounted_insurance',
]

/** Returns benefits sorted by the preferred status-match display order. */
export function sortBenefitsForDisplay<T extends { code: App.LoyaltyBenefitType }>(benefits: ReadonlyArray<T>): Array<T> {
  const indexOf = (code: App.LoyaltyBenefitType) => {
    const i = BENEFIT_DISPLAY_ORDER.indexOf(code)
    return i === -1 ? Number.MAX_SAFE_INTEGER : i
  }
  return [...benefits].sort((a, b) => indexOf(a.code) - indexOf(b.code))
}
